import React, { useEffect, useRef } from 'react';
import { FaTimes } from 'react-icons/fa';
import './LegalModal.css';

const TermsOfServiceModal = ({ isOpen, onClose }) => {
    const modalRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            modalRef.current?.focus();
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="legal-modal-overlay" onClick={handleBackdropClick}>
            <div
                className="legal-modal-container"
                ref={modalRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby="terms-modal-title"
            >
                {/* Modal Header */}
                <div className="legal-modal-header">
                    <h2 id="terms-modal-title" className="legal-modal-title">
                        Terms of Service
                    </h2>
                    <button
                        className="legal-modal-close"
                        onClick={onClose}
                        aria-label="Close terms of service"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="legal-modal-body">
                    <div className="legal-content">
                        <p className="last-updated">Last updated July 23, 2025</p>

                        <div className="legal-section">
                            <p className="legal-paragraph">
                                Please read these Terms and Conditions ("Terms", "Terms and Conditions") carefully before using the Mealzy Foods mobile application ("the App") operated by Mealzy Foods ("us", "we", or "our").
                            </p>

                            <p className="legal-paragraph">
                                Your access to and use of the App is conditioned on your acceptance of and compliance with these Terms. These Terms apply to all visitors, users, and others who access or use the App.
                            </p>

                            <p className="legal-paragraph">
                                By accessing or using the App, you agree to be bound by these Terms. If you disagree with any part of the terms, then you may not access the App.
                            </p>
                        </div>

                        <div className="legal-section">
                            <h3 className="legal-section-title">1. ACCOUNTS</h3>
                            <p className="legal-paragraph">
                                When you create an account with us, you must provide information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account on our App.
                            </p>

                            <p className="legal-paragraph">
                                You are responsible for safeguarding the password that you use to access the App and for any activities or actions under your password, whether your password is with our App or a third-party service.
                            </p>

                            <p className="legal-paragraph">
                                You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
                            </p>
                        </div>

                        <div className="legal-section">
                            <h3 className="legal-section-title">2. ORDERING AND PAYMENTS</h3>
                            <p className="legal-paragraph">
                                The App allows you to order dosa, food, and other items from our partnered dosa shops. Prices for items are as listed in the App and may change without notice.
                            </p>

                            <p className="legal-paragraph">
                                Payment for orders must be made through the payment methods available in the App. By providing your payment information, you represent and warrant that you are authorized to use the designated payment method.
                            </p>

                            <p className="legal-paragraph">
                                We reserve the right to refuse or cancel your order if fraud or an unauthorized or illegal transaction is suspected.
                            </p>
                        </div>

                        <div className="legal-section">
                            <h3 className="legal-section-title">3. DELIVERY AND PICKUP</h3>
                            <p className="legal-paragraph">
                                The App offers delivery and pickup options for orders. Delivery times are estimates and may vary based on factors such as traffic, weather, and order volume.
                            </p>

                            <p className="legal-paragraph">
                                For delivery orders, you must provide an accurate delivery address. You are responsible for ensuring someone is available to receive the order at the provided address.
                            </p>

                            <p className="legal-paragraph">
                                For pickup orders, you are responsible for collecting your order from the designated location within the specified time frame. Orders not collected within the time frame may be discarded, and no refund will be provided.
                            </p>
                        </div>

                        <div className="legal-section">
                            <h3 className="legal-section-title">4. CANCELLATIONS AND REFUNDS</h3>
                            <p className="legal-paragraph">
                                Orders cannot be cancelled once they are placed. All sales are final.
                            </p>

                            <p className="legal-paragraph">
                                Refunds may be provided at our discretion for issues such as incorrect or missing items, quality issues, or significant delays. Refund requests must be submitted through the App within 24 hours of the order.
                            </p>
                        </div>

                        <div className="legal-section">
                            <h3 className="legal-section-title">5. USER CONDUCT</h3>
                            <p className="legal-paragraph">
                                You agree not to use the App:
                            </p>
                            <p className="legal-bullet">• In any way that violates any applicable laws or regulations</p>
                            <p className="legal-bullet">• To impersonate or attempt to impersonate another person or entity</p>
                            <p className="legal-bullet">• To engage in any other conduct that restricts or inhibits anyone's use of the App</p>
                            <p className="legal-bullet">• To attempt to gain unauthorized access to any part of the App</p>
                        </div>

                        <div className="legal-section">
                            <h3 className="legal-section-title">6. INTELLECTUAL PROPERTY</h3>
                            <p className="legal-paragraph">
                                The App and its original content, features, and functionality are and will remain the exclusive property of Mealzy Foods and its licensors. The App is protected by copyright, trademark, and other laws.
                            </p>

                            <p className="legal-paragraph">
                                Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of Mealzy Foods.
                            </p>
                        </div>

                        <div className="legal-section">
                            <h3 className="legal-section-title">7. LIMITATION OF LIABILITY</h3>
                            <p className="legal-paragraph">
                                In no event shall Mealzy Foods, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
                            </p>
                            <p className="legal-bullet">• Your access to or use of or inability to access or use the App</p>
                            <p className="legal-bullet">• Any conduct or content of any third party on the App</p>
                            <p className="legal-bullet">• Any content obtained from the App</p>
                            <p className="legal-bullet">• Unauthorized access, use, or alteration of your transmissions or content</p>
                        </div>

                        <div className="legal-section">
                            <h3 className="legal-section-title">8. DISCLAIMER</h3>
                            <p className="legal-paragraph">
                                Your use of the App is at your sole risk. The App is provided on an "AS IS" and "AS AVAILABLE" basis. The App is provided without warranties of any kind, whether express or implied.
                            </p>

                            <p className="legal-paragraph">
                                Mealzy Foods does not warrant that the App will be uninterrupted, timely, secure, or error-free, or that any defects will be corrected.
                            </p>
                        </div>

                        <div className="legal-section">
                            <h3 className="legal-section-title">9. GOVERNING LAW</h3>
                            <p className="legal-paragraph">
                                These Terms shall be governed and construed in accordance with the laws of India, without regard to its conflict of law provisions.
                            </p>

                            <p className="legal-paragraph">
                                Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect.
                            </p>
                        </div>

                        <div className="legal-section">
                            <h3 className="legal-section-title">10. CHANGES TO TERMS</h3>
                            <p className="legal-paragraph">
                                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. What constitutes a material change will be determined at our sole discretion.
                            </p>

                            <p className="legal-paragraph">
                                By continuing to access or use our App after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the App.
                            </p>
                        </div>

                        <div className="legal-section">
                            <h3 className="legal-section-title">11. CONTACT US</h3>
                            <p className="legal-paragraph">
                                If you have any questions about these Terms, please contact us at:
                            </p>
                            <p className="legal-paragraph">
                                Mealzy Foods App<br />
                                <a 
                                    href="mailto:mealzyfoods@gmail.com"
                                    style={{ 
                                        color: 'inherit', 
                                        textDecoration: 'underline',
                                        textDecorationColor: 'currentColor'
                                    }}
                                    aria-label="Send email to Mealzy Foods"
                                >
                                    mealzyfoods@gmail.com
                                </a><br />
                                BLUE RIDGE<br />
                                Phase 1, Hinjawadi Rajiv Gandhi Infotech Park, Hinjawadi, Pimpri-Chinchwad, Maharashtra 411057
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TermsOfServiceModal;
